//https://blog.logrocket.com/getting-started-floating-ui/
//https://floating-ui.com/docs/tooltip
import * as React from "react";
import {useRef, useState} from "react";
import type {Placement} from "@floating-ui/react";
import {arrow, autoUpdate, flip, FloatingPortal, offset, shift, useDismiss, useFloating, useFocus, useHover, useInteractions, useMergeRefs, useRole} from "@floating-ui/react";
import styles from "./Tooltip.module.css"
import classNames from "classnames";

interface TooltipOptions {
    initialOpen?: boolean | undefined;
    placement?: Placement | undefined;
    open?: boolean | undefined;
    onOpenChange?: ((open: boolean) => void) | undefined;
}

export function useTooltip({
                               initialOpen = false,
                               placement = "top",
                               open: controlledOpen,
                               onOpenChange: setControlledOpen
                           }: TooltipOptions = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
    const arrowRef = useRef<HTMLDivElement>(null);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const data = useFloating({
        placement,

        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [offset(5), flip(), shift(), arrow({element: arrowRef}),]
    });

    const context = data.context;

    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, {role: "tooltip"});

    const interactions = useInteractions([hover, focus, dismiss, role]);

    return React.useMemo(
        () => ({
            open,
            setOpen,
            ...interactions,
            ...data,
            arrowRef
        }),
        [open, setOpen, interactions, data, arrowRef]
    );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = React.createContext<ContextType>(null);

const useTooltipContext = () => {
    const context = React.useContext(TooltipContext);

    if (context == null) {
        throw new Error("Tooltip components must be wrapped in <Tooltip />");
    }

    return context;
};

export function Tooltip({
                            children,
                            ...options
                        }: { children: React.ReactNode } & TooltipOptions) {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const tooltip = useTooltip(options);
    return (
        <TooltipContext.Provider value={tooltip}>
            {children}
        </TooltipContext.Provider>
    );
}

//https://github.com/floating-ui/floating-ui/discussions/1689
//https://codesandbox.io/s/sad-wozniak-im5tuy?file=/src/App.tsx
// Vue Tooltip component using Floating UI and TailwindCSS
// https://www.youtube.com/watch?v=kfjVorSv_rI
const TooltipArrow: React.FC<{ state: ContextType, className?: string }> = ({state, className}) => {
    const placement = state?.placement!;
    const top = (state?.middlewareData.arrow?.y ?? 0);
    const left = (state?.middlewareData.arrow?.x ?? 0);
    //const {x: arrowX, y: arrowY} = state?.middlewareData.arrow!;
    const arrowX = left;
    const arrowY = top;
    const opposedSide: any = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right"
    }[placement.split("-")[0]];
    const style = {
        position: state?.strategy,
        left: arrowX ? `${arrowX}px` : '',
        top: arrowY ? `${arrowY}px` : '',
        right: '',
        bottom: '',
        [opposedSide]: "-4px",
    }
    const classe = className ?? styles.Tooltip;
    const index = classe.indexOf('bg-');
    let bgColor = ''
    if (index >= 0) {
        const sub = classe.substring(index)
        for (let ind0 = 0; ind0 < sub.length; ++ind0) {
            if (sub[ind0] === ' ') break;
            bgColor = bgColor + sub[ind0];
        }
    }
    bgColor = bgColor || 'bg-gray-700';
    return <div className={classNames('absolute h-[8px] w-[8px] rotate-45', bgColor)} ref={state?.arrowRef} style={style}/>;
}

export const TooltipTrigger = React.forwardRef<
    HTMLElement,
    React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({children, asChild = false, ...props}, propRef) {
    const state = useTooltipContext();
    const childrenRef = (children as any).ref;
    //const ref = useMergeRefs([state.reference, propRef, childrenRef]);
    const ref = useMergeRefs([state.refs.setReference, propRef, childrenRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(
            children,
            state.getReferenceProps({
                ref,
                ...props,
                ...children.props,
                "data-state": state.open ? "open" : "closed"
            })
        );
    }

    return (
        <a
            ref={ref}
            // The user can divStyle the trigger based on the state
            data-state={state.open ? "open" : "closed"}
            {...state.getReferenceProps(props)}
        >
            {children}
        </a>
    );
});

export const TooltipContent = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>((props, propRef) => {
    const state = useTooltipContext();
    //const ref = useMergeRefs([state.floating, propRef]);
    const ref = useMergeRefs([state.refs.setFloating, propRef]);

    return (
        <FloatingPortal>
            {state.open && (
                <div>
                    <div
                        ref={ref}
                        style={{
                            position: state.strategy,
                            top: state.y ?? 0,
                            left: state.x ?? 0,
                            visibility: state.x == null ? "hidden" : "visible",
                            ...props.style
                        }}
                        {...{...state.getFloatingProps(props), children: undefined}}
                    >
                        {props.children}
                        <TooltipArrow state={state} className={props.className}/>
                    </div>
                </div>
            )}
        </FloatingPortal>
    );
});

function Uncontrolled() {
    return (
        <div className="App">
            <h1>Floating UI — Tooltip</h1>
            <Tooltip>
                <TooltipTrigger>My trigger</TooltipTrigger>
                <TooltipContent className="Tooltip">My tooltip</TooltipContent>
            </Tooltip>
        </div>
    );
}

function Controlled() {
    const [open, setOpen] = useState(false);
    return (
        <div className="App">
            <h1>Floating UI — Tooltip</h1>
            <Tooltip open={open} onOpenChange={setOpen}>
                <TooltipTrigger onClick={() => setOpen((v) => !v)}>
                    My trigger
                </TooltipTrigger>
                <TooltipContent className="Tooltip">My tooltip</TooltipContent>
            </Tooltip>
        </div>
    );
}

interface Props extends TooltipOptions {
    children: React.ReactNode,
    tooltip?: React.ReactNode | string,
    className?: string,
    hideIfNull?: boolean,
}

export const TooltipEx: React.FC<Props> = ({children, tooltip, className = '', hideIfNull = false, ...rest}) => {
    /*if (typeof tooltip === 'string' && !tooltip)
        return <></>*/
    return <>
        {tooltip && <Tooltip {...rest}>
            <TooltipTrigger>{children}</TooltipTrigger>
            <TooltipContent className={classNames(styles.Tooltip, className)}>
                {tooltip}
            </TooltipContent>
        </Tooltip>}
        {!tooltip && !hideIfNull && <>{children}</>}
    </>
}

export default TooltipEx;